/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\LogProfileBasicInfoBox.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Log Profiles module.
----------------------------------------------------------------*/


import { AlertCircle, FileText } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { useI18n } from '../../../hooks/useI18n';
import { useDebugMode } from '../../../hooks/useDebugMode';

interface Props {
    name: string;
    description: string;
    errors: Record<string, string>;
    onNameChange: (v: string) => void;
    onDescriptionChange: (v: string) => void;
}

const INPUT_CLS = 'w-full px-3 py-2 text-sm border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent';

export function LogProfileBasicInfoBox({ name, description, errors, onNameChange, onDescriptionChange }: Props) {
    const { t } = useI18n();
    const { isActive: debug } = useDebugMode();

    return (
        <FormBox title="Basic Information" icon={<FileText className="h-5 w-5" />}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
                {/* Name */}
                <SimpleTooltip content={t('logProfiles.tooltips.logProfileName')}>
                    <div className="flex flex-col">
                        <label className="text-sm font-medium text-foreground">{t('logProfiles.fields.name')} *</label>
                        {debug && <span className="text-xs text-muted-foreground mt-0.5">name</span>}
                    </div>
                </SimpleTooltip>
                <div>
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => onNameChange(e.target.value)}
                        className={`${INPUT_CLS} ${errors.name ? 'border-red-500' : 'border-border'}`}
                        placeholder={t('logProfiles.forms.placeholders.name')}
                    />
                    {errors.name && (
                        <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />{errors.name}
                        </p>
                    )}
                </div>

                {/* Description */}
                <SimpleTooltip content={t('logProfiles.tooltips.description')}>
                    <div className="flex flex-col">
                        <label className="text-sm font-medium text-foreground">{t('logProfiles.fields.description')}</label>
                        {debug && <span className="text-xs text-muted-foreground mt-0.5">description</span>}
                    </div>
                </SimpleTooltip>
                <input
                    type="text"
                    value={description}
                    onChange={(e) => onDescriptionChange(e.target.value)}
                    className={`${INPUT_CLS} border-border`}
                    placeholder={t('logProfiles.forms.placeholders.description')}
                />
            </div>
        </FormBox>
    );
}
